import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';

import CpdDropdown from '@/components/ui/CpdDropdown';
import DefaultRoleBadge from '@/components/DefaultRoleBadge/DefaultRoleBadge';
import AllFacilitiesAccessBadge from '@/components/AllFacilitiesAccessBadge/AllFacilitiesAccessBadge';
import RoleCustomPaymentTypeAccessSummary from '@/components/RoleCustomPaymentTypeAccessSummary/RoleCustomPaymentTypeAccessSummary';
import { useGetRoles } from '@/api/hooks/roles/useGetRoles';
import { useGetRole } from '@/api/hooks/roles/useGetRole';
import PermissionsPreviewTable from '@/components/PermissionsPreviewTable/PermissionsPreviewTable';

const RolesDropdown = ({
  value,
  onRoleChange = () => {},
  label = 'Role',
  size = 'small',
  fullWidth = true,
  required = false,
  disabled = false,
  error = false,
  helperText = '',
  showDescription = true,
  organizationId = null,
  showPreview = true,
  setRoleBasedFacilitiesAccess = () => {},
}) => {
  const navigate = useNavigate();
  const { roles, loading, error: apiError } = useGetRoles(
    organizationId ? { organization_id: organizationId } : {},
  );
  const { role: selectedRoleDetails, loading: roleLoading } = useGetRole(
    value || null,
  );
  const [previewOpen, setPreviewOpen] = useState(false);

  const selectedRole = roles?.find((role) => role.id === value);

  const shouldShowCustomPaymentTypeSummary = (role) =>
    Boolean(
      role?.custom_payment_type_access_enabled ||
        Number(role?.disabled_selected_custom_payment_type_count || 0) > 0,
    );

  useEffect(() => {
    setRoleBasedFacilitiesAccess(
      Boolean(selectedRole?.has_all_facilities_access),
    );
  }, [selectedRole]);

  const onChange = (e) => {
    const roleId = e.target.value;
    const role = roles?.find((role) => role.id === roleId);
    const hasRoleBasedFacilitiesAccess = Boolean(
      role?.has_all_facilities_access,
    );
    onRoleChange(roleId, hasRoleBasedFacilitiesAccess);
  };

  const renderOption = showDescription
    ? (role) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
          }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto minmax(0, 1fr)',
              columnGap: 1,
              minWidth: 0,
            }}>
            <SecurityIcon
              fontSize='small'
              color='primary'
              sx={{ alignSelf: 'center' }}
            />
            <Typography variant='body1' component='div'>
              {role.name}
            </Typography>
            {shouldShowCustomPaymentTypeSummary(role) && (
              <Box sx={{ gridColumn: 2, mt: 0.5 }}>
                <RoleCustomPaymentTypeAccessSummary role={role} />
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 0.5,
              ml: 'auto',
              alignSelf: 'center',
              flexShrink: 0,
            }}>
            <DefaultRoleBadge role={role} />
            <AllFacilitiesAccessBadge role={role} />
          </Box>
        </Box>
      )
    : null;

  const RolePreviewDialog = () => {
    if (!selectedRoleDetails) return null;

    return (
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth='md'
        fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityIcon color='primary' />
            Role Permissions Preview: {selectedRoleDetails.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity='info' sx={{ mb: 3 }}>
              This preview shows all permissions granted by the &quot;
              {selectedRoleDetails.name}&quot; role.
            </Alert>

            {selectedRoleDetails.description && (
              <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
                {selectedRoleDetails.description}
              </Typography>
            )}

            {shouldShowCustomPaymentTypeSummary(selectedRoleDetails) && (
              <Box sx={{ mb: 3 }}>
                <RoleCustomPaymentTypeAccessSummary
                  role={selectedRoleDetails}
                />
              </Box>
            )}

            <PermissionsPreviewTable
              facilityPermissions={selectedRoleDetails.facility_permissions}
              organizationPermissions={
                selectedRoleDetails.organization_permissions
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box>
      <CpdDropdown
        options={roles}
        value={value}
        onChange={onChange}
        label={label}
        size={size}
        fullWidth={fullWidth}
        required={required}
        disabled={disabled || !organizationId}
        error={error}
        helperText={
          helperText ||
          (!organizationId ? 'Please select an organization first' : '')
        }
        loading={loading}
        apiError={apiError}
        getOptionLabel={(role) => role.name}
        getOptionValue={(role) => role.id}
        renderOption={renderOption}
        placeholder='Select a role'
      />

      {selectedRole && showPreview && (
        <Alert
          severity='info'
          sx={{
            mt: 1,
            '& .MuiAlert-message': {
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              flex: 1,
            },
          }}>
          <Box>
            <Typography
              variant='body2'
              sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <strong>Selected Role:</strong>
              <Link
                component='button'
                variant='body2'
                onClick={() => navigate(`/admin/roles/${selectedRole.id}`)}>
                {selectedRole.name}
              </Link>
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {selectedRole.description}
            </Typography>
            {shouldShowCustomPaymentTypeSummary(selectedRole) && (
              <Box sx={{ mt: 1 }}>
                <RoleCustomPaymentTypeAccessSummary role={selectedRole} />
              </Box>
            )}
          </Box>
          <Button
            variant='outlined'
            size='small'
            startIcon={<VisibilityIcon />}
            onClick={() => setPreviewOpen(true)}
            disabled={roleLoading || !selectedRoleDetails}
            sx={{ flexShrink: 0 }}>
            Preview
          </Button>
        </Alert>
      )}

      <RolePreviewDialog />
    </Box>
  );
};

export default RolesDropdown;
